import React, { useState } from 'react';
import { Box, Menu, MenuItem, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CustomButton from '../v2/general/CustomButton/CustomButton';

interface SchedulesFloatingActionsProps {
  tasksCount?: number;
  onScheduleTasks?: () => void;
  onSendSchedule?: () => void;
  onSendScheduleUpcoming?: () => void;
  onPrintSchedule?: () => void;
}

const SchedulesFloatingActions = React.memo(function SchedulesFloatingActions({
  tasksCount = 0,
  onScheduleTasks,
  onSendSchedule,
  onSendScheduleUpcoming,
  onPrintSchedule,
}: SchedulesFloatingActionsProps) {
  const [sendMenuAnchor, setSendMenuAnchor] = useState<null | HTMLElement>(
    null,
  );

  const isSendMenuOpen = Boolean(sendMenuAnchor);

  const handleOpenSendMenu = (event: React.MouseEvent<HTMLElement>) => {
    setSendMenuAnchor(event.currentTarget);
  };

  const handleCloseSendMenu = () => {
    setSendMenuAnchor(null);
  };

  const handleSendScheduleToday = () => {
    onSendSchedule?.();
    handleCloseSendMenu();
  };

  const handleSendScheduleUpcoming = () => {
    onSendScheduleUpcoming?.();
    handleCloseSendMenu();
  };

  const handlePrintSchedule = () => {
    onPrintSchedule?.();
    handleCloseSendMenu();
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: '16px',
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '14px',
          pointerEvents: 'auto',
        }}
      >
        <CustomButton
          variant='contained'
          size='large'
          onClick={onScheduleTasks}
          sx={{
            backgroundColor: '#092056',
            color: '#FFFFFF',
            padding: '10px 18px',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#0B2C67',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                fontSize: '14px',
                color: '#FFFFFF',
              }}
            >
              {tasksCount > 0
                ? `Tarefas da Escala (${tasksCount})`
                : 'Tarefas da Escala'}
            </Typography>
            <Box
              sx={{
                width: '20px',
                height: '20px',
                borderRadius: '999px',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#FFFFFF' }} />
            </Box>
          </Box>
        </CustomButton>
        <CustomButton
          variant='contained'
          size='large'
          onClick={handleOpenSendMenu}
          endIcon={
            <KeyboardArrowDownIcon sx={{ color: '#FFFFFF', fontSize: 24 }} />
          }
          aria-controls={isSendMenuOpen ? 'send-schedule-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={isSendMenuOpen ? 'true' : undefined}
          sx={{
            backgroundImage:
              'linear-gradient(90.126deg, rgba(9, 32, 86, 1) 0%, rgba(0, 165, 255, 1) 99.972%)',
            color: '#FFFFFF',
            padding: '8px 22px',
            fontFamily: 'Poppins',
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '26px',
            textTransform: 'none',
            borderRadius: '4px',
            '&:hover': {
              backgroundImage:
                'linear-gradient(90.126deg, rgba(9, 32, 86, 1) 0%, rgba(0, 165, 255, 1) 99.972%)',
            },
          }}
        >
          Enviar Escala
        </CustomButton>
        <Menu
          id='send-schedule-menu'
          anchorEl={sendMenuAnchor}
          open={isSendMenuOpen}
          onClose={handleCloseSendMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              backgroundColor: '#FFFFFF',
              borderRadius: '4px',
              boxShadow:
                '0px 3px 14px 2px rgba(9, 32, 86, 0.12), 0px 8px 10px 1px rgba(9, 32, 86, 0.14), 0px 5px 5px -3px rgba(9, 32, 86, 0.2)',
              minWidth: '220px',
            },
          }}
          MenuListProps={{
            sx: {
              padding: '8px 0',
            },
          }}
        >
          <MenuItem onClick={handleSendScheduleToday} sx={{ padding: '6px 16px' }}>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: 1.5,
                color: '#092056',
              }}
            >
              Enviar Escala do dia
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={handleSendScheduleUpcoming}
            sx={{ padding: '6px 16px' }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: 1.5,
                color: '#092056',
              }}
            >
              Enviar Escala do dia e próximos
            </Typography>
          </MenuItem>
          <MenuItem onClick={handlePrintSchedule} sx={{ padding: '6px 16px' }}>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: 1.5,
                color: '#092056',
              }}
            >
              Imprimir Escala
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
});

export default SchedulesFloatingActions;
